import { useState } from 'react';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { importLeads, exportLeadsUrl } from '@/lib/api';
import { Upload, Download, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ImportExport() {
    const [file, setFile] = useState(null);
    const [replace, setReplace] = useState(false);
    const [uploading, setUploading] = useState(false);

    const submit = async () => {
        if (!file) { toast.error('Choose an .xlsx file first'); return; }
        setUploading(true);
        try {
            const r = await importLeads(file, replace);
            toast.success(`Imported ${r.inserted} leads`);
            setFile(null);
        } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
        setUploading(false);
    };

    return (
        <div>
            <PageHeader title="Import / Export" subtitle="Excel-driven CRM sync" />
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="surface-1 rounded-md p-6" data-testid="import-panel">
                    <div className="flex items-center gap-2 mb-2">
                        <Upload className="w-4 h-4 text-[#016a6d]" />
                        <div className="overline">Import Excel</div>
                    </div>
                    <h3 className="font-display text-xl font-bold text-slate-900 mb-3">Upload CRM Sheet</h3>
                    <p className="text-sm text-slate-500 mb-5">Upload an .xlsx file with a &ldquo;Customer Database&rdquo; sheet using the same column headers as your master template.</p>
                    <label className="block cursor-pointer">
                        <div className="border border-dashed border-[#E2E8F0] rounded-md p-8 text-center hover:border-[#016a6d] transition-colors" data-testid="dropzone">
                            <FileSpreadsheet className="w-8 h-8 text-slate-500 mx-auto mb-3" />
                            <div className="text-sm text-slate-900">{file ? file.name : 'Click to select .xlsx file'}</div>
                            <div className="text-xs text-slate-500 mt-1">CRM_Pipeline_Dashboard.xlsx</div>
                        </div>
                        <input type="file" accept=".xlsx" className="hidden" onChange={e => setFile(e.target.files?.[0])} data-testid="file-input" />
                    </label>
                    <label className="flex items-center gap-2 mt-4 text-sm text-slate-700">
                        <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} data-testid="replace-checkbox" className="accent-[#016a6d]" />
                        Replace all existing leads
                    </label>
                    {replace && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-[#FF9F0A]">
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>All existing lead records will be deleted before import.</span>
                        </div>
                    )}
                    <Button onClick={submit} disabled={uploading || !file} data-testid="btn-import" className="w-full mt-5 bg-[#016a6d] hover:bg-[#014e50]">
                        {uploading ? 'Importing…' : 'Import Leads'}
                    </Button>
                </div>

                <div className="surface-1 rounded-md p-6" data-testid="export-panel">
                    <div className="flex items-center gap-2 mb-2">
                        <Download className="w-4 h-4 text-[#34C759]" />
                        <div className="overline">Export</div>
                    </div>
                    <h3 className="font-display text-xl font-bold text-slate-900 mb-3">Download Current CRM</h3>
                    <p className="text-sm text-slate-500 mb-5">Download all leads with computed weighted value & pipeline days in the same format as the master template.</p>
                    <a href={exportLeadsUrl()} data-testid="link-export">
                        <Button className="w-full bg-[#2d6b3d] hover:bg-[#255c33] gap-2">
                            <Download className="w-4 h-4" /> Download .xlsx
                        </Button>
                    </a>
                </div>
            </div>
        </div>
    );
}
